import React, { useContext, useState, useEffect } from "react";
import { RemindersContext } from "../contexts/RemindersContext";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { differenceInDays, isAfter, format, isSameDay } from "date-fns";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

// Helper to calculate streak from completedDates
const calculateStreak = (completedDates) => {
  if (!completedDates) return 0;

  const dates = Object.keys(completedDates)
    .filter((key) => completedDates[key])
    .sort()
    .map((dateStr) => new Date(dateStr));

  if (dates.length === 0) return 0;

  let currentStreak = 0;
  let lastDate = null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let checkDate = new Date();
  checkDate.setHours(0, 0, 0, 0);

  for (let i = 0; i < dates.length; i++) {
    const date = dates[dates.length - 1 - i];
    date.setHours(0, 0, 0, 0);

    if (i === 0) {
      if (date.getTime() === today.getTime()) {
        currentStreak = 1;
        checkDate.setDate(checkDate.getDate() - 1);
        continue;
      }
      if (
        date.getTime() < today.getTime() &&
        differenceInDays(today, date) === 1
      ) {
        currentStreak = 1;
        checkDate.setDate(checkDate.getDate() - 1);
        continue;
      }

      if (
        date.getTime() < today.getTime() &&
        differenceInDays(today, date) > 1
      ) {
        return 0;
      }
    }

    const expectedPrevDay = new Date(lastDate || checkDate);
    expectedPrevDay.setDate(expectedPrevDay.getDate() - 1);

    if (lastDate && date.getTime() === expectedPrevDay.getTime()) {
      currentStreak++;
    } else if (lastDate && date.getTime() !== lastDate.getTime()) {
      break;
    }

    lastDate = date;
  }

  let streak = 0;
  let lastCompletedDay = null;

  const completedDateStrings = new Set(
    dates.map((d) => format(d, "yyyy-MM-dd"))
  );

  let pointerDate = new Date();
  pointerDate.setHours(0, 0, 0, 0);

  let isTodayCompleted = completedDateStrings.has(
    format(pointerDate, "yyyy-MM-dd")
  );

  if (!isTodayCompleted) {
    pointerDate.setDate(pointerDate.getDate() - 1);
    if (!completedDateStrings.has(format(pointerDate, "yyyy-MM-dd"))) {
      return 0;
    }
  }

  while (true) {
    const dateStr = format(pointerDate, "yyyy-MM-dd");

    if (completedDateStrings.has(dateStr)) {
      streak++;
    } else {
      break;
    }

    pointerDate.setDate(pointerDate.getDate() - 1);

    if (streak > 365) break;
  }

  return streak;
};

// ---------------- Component ----------------
export default function DashboardReminders() {
  const { t } = useTranslation();
  const { userId } = useAuth();

  const getUserCollection = (collectionName) => {
    if (!userId) return null;
    return collection(db, "users", userId, collectionName);
  };

  const { reminders, todayReminders } = useContext(RemindersContext);
  const [activeTab, setActiveTab] = useState("today");
  const [habitStreaks, setHabitStreaks] = useState([]);

  // 🔹 Find upcoming reminders (after today)
  const upcomingReminders = reminders
    .filter((r) => isAfter(r.date, new Date()))
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  // 🔹 Habit Streaks Listener
  useEffect(() => {
    if (!userId) return;
    const habitsRef = getUserCollection("habits");
    if (!habitsRef) return;

    const unsub = onSnapshot(habitsRef, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const streaks = data.map((habit) => ({
        id: habit.id,
        name: habit.name,
        days: habit.days || [],
        streak: calculateStreak(habit.completedDates),
      }));

      setHabitStreaks(streaks);
    });

    return () => unsub();
  }, [userId]);

  // Function to calculate days left
  const calculateDaysLeft = (reminderDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(reminderDate);
    date.setHours(0, 0, 0, 0);
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Wrapper>
      {/* ---------------- 1. Reminder Tabs Section ---------------- */}
      {/* <RemindersSection>
        <SectionTitle>{t("reminders.title")}</SectionTitle>
        <TabContainer>
          <TabButton
            active={activeTab === "today"}
            onClick={() => setActiveTab("today")}
          >
            {t("reminders.today")}
          </TabButton>
          <TabButton
            active={activeTab === "upcoming"}
            onClick={() => setActiveTab("upcoming")}
          >
            {t("reminders.upcoming")}
          </TabButton>
        </TabContainer>

        <ListWrapper>
          {activeTab === "today" ? (
            todayReminders.length === 0 ? (
              <EmptyText>{t("reminders.noReminders")}</EmptyText>
            ) : (
              <List>
                {todayReminders.map((reminder) => (
                  <ReminderItem key={reminder.id}>{reminder.text}</ReminderItem>
                ))}
              </List>
            )
          ) : upcomingReminders.length === 0 ? (
            <EmptyText>{t("reminders.noUpcoming")}</EmptyText>
          ) : (
            <List>
              {upcomingReminders.map((reminder) => {
                const daysLeft = calculateDaysLeft(reminder.date);
                return (
                  <UpcomingItem key={reminder.id}>
                    <span>{reminder.text}</span>
                    <DaysLeft>
                      {daysLeft === 1
                        ? t("reminders.tomorrow")
                        : `${daysLeft} ${t("reminders.days")}`}
                    </DaysLeft>
                  </UpcomingItem>
                );
              })}
            </List>
          )}
        </ListWrapper>
      </RemindersSection>

      <Divider /> */}

      {/* ---------------- 2. Habit Streaks Section ---------------- */}
      <StreaksSection>
        <SectionTitle>{t("streaks.title")}</SectionTitle>
        <StreaksList>
          {habitStreaks.length === 0 ? (
            <EmptyText>{t("streaks.noHabits")}</EmptyText>
          ) : (
            habitStreaks.map((h) => (
              <StreakItem key={h.id}>
                <span>{h.name}</span>
                <StreakCount $count={h.streak}>
                  {h.streak} {t("streaks.day", { count: h.streak })}
                </StreakCount>
              </StreakItem>
            ))
          )}
        </StreaksList>
      </StreaksSection>
    </Wrapper>
  );
}

/* ---------------- Styled Components ---------------- */

const Wrapper = styled.div.attrs((props) => ({
  style: {
    width: props.$width,
  },
}))`
  border-radius: 16px;
  height: stretch;
  display: flex;
  flex-direction: column;
  gap: 1em;
  color: var(--fontPrimaryColor);
  transition: 0.5s;
  min-height: 300px;
  /* width: 20%;
  @media (max-width: 1850px) {
    width: 27%;
  }
  @media (max-width: 1400px) {
    width: 33%;
  }
  @media (max-width: 1050px) {
    width: 100%;
  } */
`;

const RemindersSection = styled.div`
  /* height: 40%; */
  display: flex;
  flex-direction: column;
  background: var(--secondaryBackgroundColor);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 20px;
  gap: 15px;
  border-radius: 20px;
  transition: 0.5s;
`;

const StreaksSection = styled.div`
  display: flex;
  transition: 0.5s;
  flex-direction: column;
  gap: 10px;
  background: var(--secondaryBackgroundColor);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 20px;
  border-radius: 20px;
  height: stretch;
  min-height: 300px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 5px;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  padding: 5px;
  margin-bottom: 5px;
  justify-content: space-between;
  transition: 0.5s;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 8px 5px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  background: ${({ active }) => (active ? "#495269" : "transparent")};
  color: ${({ active }) => (active ? "white" : "var(--fontSecondaryColor)")};
  text-align: center;

  &:hover:not(:active) {
    background: ${({ active }) =>
      active ? "var(--primaryColor)" : "rgba(0,0,0,0.05)"};
  }
`;

const ListWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 5px;
  min-height: 100px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
`;

const ReminderItem = styled.li`
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  list-style: none;
  color: blue;
`;

const UpcomingItem = styled(ReminderItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DaysLeft = styled.span`
  font-size: 12px;
  color: #b6b6b6;
  white-space: nowrap;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--borderColor);
  margin: 0;
`;

const EmptyText = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 14px;
  text-align: center;
  padding: 10px 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-weight: 500;
  transition: 0.5s;
  margin: 1em 0;
  font-size: 18px;
  font-family: "Montserrat", sans-serif;
`;

const StreaksList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  max-height: 380px;
  overflow-y: scroll;
`;

const StreakItem = styled.li`
  transition: 0.5s;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  font-size: 14px;
  list-style: none;
`;

const StreakCount = styled.span.attrs((props) => ({
  style: {
    background:
      props.$count > 7
        ? "#10b981"
        : props.$count > 0
        ? "var(--primaryColor)"
        : "#8b8b8b",
  },
}))`
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
  color: white;
  font-size: 12px;
`;
